import { useState } from 'react';
import { Box, Flex, Heading, Badge, Select, Icon, Text, Grid } from '@chakra-ui/react';
import { LinkIcon } from '@chakra-ui/icons';
import { NetInfo, StatsForNerds } from '../../types/stats';
import { parseMemoryMB } from '../../utils/stats';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';

interface NetworkCardProps {
  data: NetInfo[];
  history: StatsForNerds[];
  paused: boolean;
}

export function NetworkCard({ data, history, paused }: NetworkCardProps) {
  const [selectedInterface, setSelectedInterface] = useState(data[0]?.iface || '');

  if (!data || data.length === 0) {
    return (
      <Box borderWidth="1px" borderRadius="2xl" p={4} position="relative" overflow="hidden">
        {paused && (
          <Badge position="absolute" top={2} right={2} colorScheme="yellow">Paused</Badge>
        )}
        
        <Flex as="header" alignItems="center" justifyContent="space-between" pb={2}>
          <Heading as="h3" size="sm" display="flex" alignItems="center" gap={2}>
            <Icon as={LinkIcon} />
            Network
          </Heading>
        </Flex>
        
        <Text textAlign="center" py={4} color="gray.500">
          No network interfaces found
        </Text>
      </Box>
    );
  }

  const selectedNetData = data.find(net => net.iface === selectedInterface) || data[0];
  
  // Generate chart data from history
  const chartData = history.slice(-10).map((stats, index) => {
    const netInterface = stats.network?.find(n => n.iface === selectedInterface);
    if (!netInterface) return { index, rx: 0, tx: 0 };
    
    return {
      index,
      rx: parseMemoryMB(netInterface.rx),
      tx: parseMemoryMB(netInterface.tx)
    };
  });

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes.toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(1)} GB`;
  };

  return (
    <Box borderWidth="1px" borderRadius="lg" p={4} position="relative" overflow="hidden">
      {paused && (
        <Badge position="absolute" top={2} right={2} colorScheme="yellow">Paused</Badge>
      )}
      
      <Flex as="header" alignItems="center" justifyContent="space-between" pb={2}>
        <Heading as="h3" size="sm" display="flex" alignItems="center" gap={2}>
          <Icon as={LinkIcon} />
          Network ({data.length})
        </Heading>
        {data.length > 1 && (
          <Select value={selectedInterface} onChange={(e) => setSelectedInterface(e.target.value)} size="sm" w="120px">
            {data.map((net) => (
              <option key={net.iface} value={net.iface}>
                {net.iface}
              </option>
            ))}
          </Select>
        )}
      </Flex>
      
      <Box>
        {/* Current Stats */}
        <Grid templateColumns="repeat(2, 1fr)" gap={4} textAlign="center">
          <Box>
            <Text fontSize="lg" fontWeight="bold" fontFamily="mono" color="green.500">
              {selectedNetData.rx}
            </Text>
            <Text fontSize="xs" color="gray.500">RX (Received)</Text>
          </Box>
          <Box>
            <Text fontSize="lg" fontWeight="bold" fontFamily="mono" color="yellow.500">
              {selectedNetData.tx}
            </Text>
            <Text fontSize="xs" color="gray.500">TX (Transmitted)</Text>
          </Box>
        </Grid>

        {/* Interface Info */}
        <Flex alignItems="center" justifyContent="space-between" fontSize="sm" mt={4}>
          <Text color="gray.500">Interface:</Text>
          <Badge variant="outline" fontFamily="mono" fontSize="xs">
            {selectedNetData.iface}
          </Badge>
        </Flex>

        {/* Mini Chart */}
        {chartData.length > 1 && (
          <Box mt={4}>
            <Text fontSize="sm" color="gray.500">Traffic History</Text>
            <Box h="80px" mt={2}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <XAxis dataKey="index" hide />
                  <YAxis hide />
                  <Tooltip
                    formatter={(value: number, name: string) => [
                      formatBytes(value),
                      name === 'rx' ? 'Received' : 'Transmitted'
                    ]}
                    labelFormatter={() => ''}
                    contentStyle={{
                      backgroundColor: '#2D3748', // gray.700
                      border: '1px solid #4A5568', // gray.600
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="rx"
                    stroke="#48BB78" // green.400
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="tx"
                    stroke="#D69E2E" // yellow.500
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
            <Flex justifyContent="center" gap={4} fontSize="xs" mt={2}>
              <Flex alignItems="center" gap={1}>
                <Box w={2} h={2} bg="green.400" borderRadius="full"></Box>
                <Text color="gray.500">RX</Text>
              </Flex>
              <Flex alignItems="center" gap={1}>
                <Box w={2} h={2} bg="yellow.500" borderRadius="full"></Box>
                <Text color="gray.500">TX</Text>
              </Flex>
            </Flex>
          </Box>
        )}
      </Box>
    </Box>
  );
}
